/**
 * MobileLeavesTopMenu
 *
 * Mobile-optimized top navigation for LeavesView.
 * Replaces the Bootstrap Row/Col toolbar when isMobile === 2.
 * Uses MUI IconButton for actions and renders TeamSelector at full width.
 */
import React from "react";
import { Box, IconButton, Tooltip } from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import Langs from "../../../app/lang/langs";
import { TeamSelector } from "../../../components/teams/TeamSelector";

const TRANSITION_SMOOTH = "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)";

const iconButtonSx = {
  minWidth: 44,
  minHeight: 44,
  borderRadius: "12px",
  transition: TRANSITION_SMOOTH,
  "&:hover": { backgroundColor: "rgba(0,0,0,0.06)", transform: "scale(1.06)" },
  "&:active": { transform: "scale(0.96)" },
};

const primaryIconButtonSx = {
  ...iconButtonSx,
  "&:hover": {
    backgroundColor: "rgba(255,144,21,0.08)",
    transform: "scale(1.06)",
    boxShadow: "0 2px 8px rgba(255,144,21,0.15)",
  },
};

/**
 * @param {Object} props
 * @param {boolean} props.canExport  - whether to show the export button
 * @param {Function} props.onAddLeave - opens the leave request form
 * @param {Function} props.onExport   - triggers CSV/Excel export
 */
export default function MobileLeavesTopMenu({
  canExport,
  onAddLeave,
  onExport,
}) {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        width: "100%",
        bgcolor: "rgba(255,255,255,0.72)",
        backdropFilter: "blur(16px)",
        WebkitBackdropFilter: "blur(16px)",
        borderBottom: "1px solid rgba(0,0,0,0.06)",
        boxShadow:
          "0 1px 8px rgba(0,0,0,0.04), 0 0.5px 2px rgba(0,0,0,0.03)",
        px: 1,
        pt: 0.5,
        pb: 1,
      }}
    >
      {/* Title + action buttons row */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          minHeight: 48,
          px: 0.5,
        }}
      >
        <Box
          sx={{
            fontWeight: 650,
            fontSize: "1.1rem",
            letterSpacing: "-0.02em",
            pl: 0.5,
          }}
        >
          <Langs str="Leaves" />
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          {/* Add leave button */}
          <Tooltip title={<Langs str="Request Leave" />}>
            <IconButton
              onClick={onAddLeave}
              size="large"
              color="warning"
              sx={primaryIconButtonSx}
            >
              <AddCircleOutlineIcon />
            </IconButton>
          </Tooltip>

          {/* Export button (conditional) */}
          {canExport && (
            <Tooltip title="Export">
              <IconButton
                onClick={onExport}
                size="large"
                sx={iconButtonSx}
              >
                <FileDownloadOutlinedIcon />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>

      {/* TeamSelector row */}
      <Box sx={{ px: 0.5 }}>
        <TeamSelector includeMyTeam />
      </Box>
    </Box>
  );
}
